import { DOMType, throttle, transparentTag as css, } from '@tencent/exeditor3-core';
import '@tencent/exeditor3-ui-kit/dist/toolbar-button.js';
import * as zIndex from '@tencent/exeditor3-ui-kit/dist/resizer/zIndex.js';
import { createResizer } from '@tencent/exeditor3-ui-kit/dist/resizer/index.js';
import { pp, px, fixFloatNumber, } from '@tencent/exeditor3-ui-kit/dist/resizer/utils.js';
import { pickFile } from '@tencent/exeditor3-ui-kit/dist/utils.js';
import { createIcon } from '@tencent/exeditor3-ui-kit/dist/icon.js';
import { FilePlugin, uploadingContainerClassName, fileStyles, } from '@tencent/exeditor3-template-plugin-file';
const containerClassName = 'exeditor-video';
const styles = css `
  ${fileStyles}
  span.exeditor-video {
    display: inline-block;
  }
  .exeditor-video-container-inline {
    display: inline-block;
    margin: 0 2px;
  }
  .exeditor-video-container-inline video {
    margin: 0;
  }
`;
const pick = (obj, array) => {
    const result = {};
    array.forEach((key) => {
        if (Object.hasOwnProperty.call(obj, key)) {
            result[key] = obj[key];
        }
    });
    return result;
};
const getVideoMetadata = (src) => {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.addEventListener('loadedmetadata', () => {
            resolve({
                width: video.videoWidth,
                height: video.videoHeight,
            });
            video.remove();
        });
        video.src = src;
    });
};
const parseVideoInfoFromNode = (node) => {
    let video;
    if (node instanceof HTMLVideoElement) {
        video = node;
    }
    else {
        video = node.querySelector('video');
    }
    if (!video) {
        return false;
    }
    return {
        src: video.src || node.dataset.src || '',
        title: video.title || node.dataset.title || '',
        width: video.width,
        height: video.height,
        widthPercent: parseFloat(video.dataset.widthPercent || '0') / 100,
    };
};
const assignVideoAttributes = (video, info) => {
    const { src, width, height } = info;
    video.src = src;
    video.controls = true;
    if (width) {
        video.width = width;
    }
    if (height) {
        video.height = height;
    }
};
const defaultOption = {
    upload: () => {
        throw new Error('upload() method has not been set.');
    },
    accept: 'video/*',
    acceptFile: (file) => /^video\//i.test(file.type),
};
export class VideoPlugin extends FilePlugin {
    constructor(options = {}) {
        super();
        this.name = 'video';
        this.nameCN = '视频';
        this.spec = {
            type: DOMType.BLOCK,
            matchSelector: `.${uploadingContainerClassName}[data-plugin="video"], video`,
            attrs: {
                info: {
                    parse: parseVideoInfoFromNode,
                },
                ...this.fileAttrsSpec,
            },
            toDOM: (node) => {
                const isInline = this.options.inline;
                const { uploading, info } = node.attrs;
                if (uploading) {
                    return this.wrapUploadingDOMRule(node.attrs, {
                        tag: 'video',
                        attributes: {
                            src: info.src,
                        },
                    });
                }
                let style = undefined;
                const attributes = pick(info, ['src', 'width', 'height']);
                if (!isInline && info.widthPercent) {
                    const percent = pp(info.widthPercent);
                    style = `width: ${percent}`;
                    attributes['data-width-percent'] = percent;
                }
                return {
                    tag: isInline ? 'span' : 'div',
                    attributes: {
                        class: containerClassName,
                    },
                    children: {
                        tag: 'video',
                        attributes: {
                            ...attributes,
                            style,
                            controls: '',
                            'data-inline': isInline ? '' : undefined,
                        },
                    },
                };
            },
            draggable: true,
        };
        this.editView = (initNode, getPos) => {
            let node = initNode;
            let readOnly = this.editor.isReadOnly();
            if (node.attrs.uploading) {
                return this.editPlaceholderView(node, getPos);
            }
            if (this.isNeedToUploadBySrc(node.attrs.info)) {
                this.uploadBySrc(node.attrs.info, getPos);
                return this.editPlaceholderView(node, getPos);
            }
            const isInline = this.options.inline;
            const video = document.createElement('video');
            assignVideoAttributes(video, node.attrs.info);
            if (isInline) {
                video.dataset.inline = '';
            }
            const holder = document.createElement('div');
            Object.assign(holder.style, {
                position: 'relative',
                width: 'fit-content',
                margin: isInline ? '0' : '1em auto',
            });
            holder.appendChild(video);
            const container = document.createElement(isInline ? 'span' : 'div');
            container.classList.add('no-outline');
            if (isInline) {
                container.classList.add('exeditor-video-container-inline');
            }
            Object.assign(container.style, {
                position: 'relative',
                'z-index': zIndex.box,
            });
            container.appendChild(holder);
            const handleUpdate = () => {
                const { info } = node.attrs;
                if (readOnly) {
                    return;
                }
                const rect = container.getBoundingClientRect();
                if (info.widthPercent) {
                    holder.style.width = pp(info.widthPercent);
                }
                else {
                    holder.style.width = 'fit-content';
                }
                updateResizer(this.options.inline
                    ? {}
                    : {
                        max: rect.width,
                    });
            };
            const handleSizeChange = (result) => {
                const pos = getPos();
                const { info } = node.attrs;
                if (!isInline) {
                    const containerWidth = container.getBoundingClientRect().width;
                    const widthPercent = result.width / containerWidth;
                    holder.style.width = pp(widthPercent);
                    video.style.width = '100%';
                    this.editor.patchNodeAttr(pos, { info: { ...info, widthPercent } });
                }
                else {
                    video.style.width = px(result.width);
                    video.style.height = px(result.height);
                    this.editor.patchNodeAttr(pos, {
                        info: {
                            ...info,
                            width: fixFloatNumber(result.width),
                            height: fixFloatNumber(result.height),
                        },
                    });
                }
            };
            const handleReadOnlyChange = (readonly) => {
                readOnly = readonly;
            };
            const hideOutline = () => {
                holder.style.removeProperty('outline');
                resizer.style.display = 'none';
            };
            const showOutline = () => {
                if (readOnly) {
                    holder.style.outline = 'var(--exeditor-selected-outline)';
                }
                else {
                    resizer.style.display = 'block';
                }
            };
            const handleSelect = () => {
                handleUpdate();
                showOutline();
                video.style.setProperty('z-index', zIndex.box.toString());
            };
            const handleDeSelect = () => {
                hideOutline();
                video.style.removeProperty('z-index');
            };
            if (node.attrs.info.widthPercent) {
                video.style.width = '100%';
                holder.style.width = pp(node.attrs.info.widthPercent);
            }
            const { resizer, update: updateResizer } = createResizer({
                target: video,
                onResizeEnd: handleSizeChange,
                keepAspectRatio: true,
            });
            Object.assign(resizer.style, {
                display: 'none',
                'pointer-events': 'none',
            });
            const handleResizeThrottle = throttle(handleUpdate, 15);
            let lastContainerWidth = 0;
            const rsob = new ResizeObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.contentRect) {
                        const rectWidth = entry.contentRect.width;
                        if (lastContainerWidth !== rectWidth) {
                            lastContainerWidth = rectWidth;
                            handleResizeThrottle();
                        }
                    }
                });
            });
            video.addEventListener('loadedmetadata', () => {
                holder.appendChild(resizer);
                rsob.observe(container);
                handleUpdate();
            });
            this.editor.on('readonly', handleReadOnlyChange);
            return {
                dom: container,
                update: (updatedNode) => {
                    node = updatedNode;
                    const { src } = node.attrs.info;
                    if (src !== video.src) {
                        return false;
                    }
                    handleUpdate();
                    return true;
                },
                selectNode: handleSelect,
                deselectNode: handleDeSelect,
                destroy: () => {
                    this.editor.off('readonly', handleReadOnlyChange);
                    rsob.unobserve(container);
                    rsob.disconnect();
                },
            };
        };
        this.options = {
            ...defaultOption,
            ...options,
        };
        this.spec.type = this.options.inline ? DOMType.INLINE_BLOCK : DOMType.BLOCK;
    }
    capabilities() {
        const capabilities = super.capabilities();
        return {
            ...capabilities,
            styles,
        };
    }
    initEditor(editor) {
        this.editor = editor;
    }
    renderToolbarItem() {
        const btn = document.createElement('exeditor-toolbar-button');
        Object.assign(btn, {
            label: this.editor.t('视频'),
            onclick: () => {
                pickFile(this.options.accept, true).then((files) => {
                    if (!(files === null || files === void 0 ? void 0 : files.length)) {
                        return;
                    }
                    this.editor.focus();
                    this.insertByFiles(Array.from(files));
                });
            },
        }).append(createIcon({
            name: this.name,
            editor: this.editor,
            fallback: () => import('./icon.js').then((m) => m.videoIcon),
        }));
        return btn;
    }
    createContentElement(info) {
        const video = document.createElement('video');
        video.src = info.src;
        return video;
    }
    async getMetadata(result) {
        return getVideoMetadata(typeof result === 'string' ? result : result.src);
    }
}
//# sourceMappingURL=index.js.map